import React, { useState, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getTemplate, previewTemplate, getAnnexTemplates, Template, SchemaField } from '../../templates/services/TemplatesService';
import { createAnnexDocument } from '../../employees/services/DocumentService';
import { PreviewPane } from './PreviewPane';
import { personalakteStyles as ps, colors, shadows } from '../../employees/styles/personalakteStyles';

interface AnnexQuickCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  employeeId: number;
  templateKey?: string;
  templateName?: string;
  onSuccess?: (document: any) => void;
}

export function AnnexQuickCreateModal({
  isOpen,
  onClose,
  employeeId,
  templateKey,
  templateName,
  onSuccess,
}: AnnexQuickCreateModalProps) {
  const [showingTemplateSelector, setShowingTemplateSelector] = useState(!templateKey);
  const [availableTemplates, setAvailableTemplates] = useState<any[]>([]);
  const [selectedTemplateKey, setSelectedTemplateKey] = useState<string | undefined>(templateKey);
  const [selectedTemplateName, setSelectedTemplateName] = useState<string | undefined>(templateName);

  const [template, setTemplate] = useState<Template | null>(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [previewSrc, setPreviewSrc] = useState<string | undefined>(undefined);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setTemplate(null);
      setFormValues({});
      setPreviewSrc(undefined);
      setShowingTemplateSelector(!templateKey);
      setSelectedTemplateKey(templateKey);
      setSelectedTemplateName(templateName);
      setAvailableTemplates([]);
      return;
    }

    if (!selectedTemplateKey) {
      setLoadingTemplate(true);
      setShowingTemplateSelector(true);

      (async () => {
        try {
          const templates = await getAnnexTemplates();
          setAvailableTemplates(templates);
        } catch (err) {
          toast.error('Templates konnten nicht geladen werden');
        } finally {
          setLoadingTemplate(false);
        }
      })();
      return;
    }

    setLoadingTemplate(true);
    setShowingTemplateSelector(false);

    (async () => {
      try {
        const annexTemplates = await getAnnexTemplates();
        const matchingTemplate = annexTemplates.find(t => t.template_key === selectedTemplateKey);

        if (!matchingTemplate) {
          toast.error('Template konnte nicht gefunden werden');
          onClose();
          return;
        }

        const fullTemplate = await getTemplate(matchingTemplate.id);
        setTemplate(fullTemplate);
      } catch (err) {
        toast.error('Template konnte nicht geladen werden');
        onClose();
      } finally {
        setLoadingTemplate(false);
      }
    })();
  }, [isOpen, selectedTemplateKey, templateKey, templateName, onClose]);

  useEffect(() => {
    if (!template) return;

    const initialValues: Record<string, any> = {};
    [...(template.schema.required_fields || []), ...(template.schema.optional_fields || [])].forEach(field => {
      if (field.default_value !== undefined) {
        initialValues[field.key] = field.default_value;
      }
    });
    setFormValues(initialValues);
  }, [template]);

  const handlePreview = async () => {
    if (!template) return;

    setLoadingPreview(true);
    try {
      const res = await previewTemplate(template.id, {
        employee_id: employeeId,
        variables: formValues,
      });

      if (res.pdf_url) {
        setPreviewSrc(res.pdf_url);
      }
    } catch (err: any) {
      console.error('Preview failed:', err);
      toast.error('Preview konnte nicht erstellt werden');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleCreate = async () => {
    if (!template || !selectedTemplateKey) return;

    setCreating(true);
    try {
      const createdDocument = await createAnnexDocument(employeeId, {
        template_id: selectedTemplateKey,
        variables: formValues,
      });


      toast.success('Dokument erfolgreich erstellt');
      onSuccess?.(createdDocument);
      onClose();
    } catch (err: any) {
      const message = err?.response?.data?.detail || err?.message || 'Erstellen fehlgeschlagen';
      toast.error(`Fehler: ${message}`);
    } finally {
      setCreating(false);
    }
  };

  const renderField = (field: SchemaField) => {
    const value = formValues[field.key] || '';

    return (
      <div key={field.key} style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: colors.navy }}>
          {field.label}
        </label>
        {field.type === 'text' ? (
          <textarea
            value={value}
            onChange={(e) => setFormValues(prev => ({ ...prev, [field.key]: e.target.value }))}
            rows={4}
            style={{
              ...ps.inputStyle,
            }}
          />
        ) : field.type === 'select' && field.options ? (
          <select
            value={value}
            onChange={(e) => setFormValues(prev => ({ ...prev, [field.key]: e.target.value }))}
            style={ps.inputStyle}
          >
            <option value="">-- Auswählen --</option>
            {field.options.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        ) : field.type === 'date' ? (
          <input
            type="date"
            value={value}
            onChange={(e) => setFormValues(prev => ({ ...prev, [field.key]: e.target.value }))}
            style={ps.inputStyle}
          />
        ) : (
          <input
            type={field.type === 'number' || field.type === 'decimal' ? 'number' : 'text'}
            value={value}
            onChange={(e) => setFormValues(prev => ({ ...prev, [field.key]: e.target.value }))}
            style={ps.inputStyle}
          />
        )}
        {field.help_text && (
          <div style={{ fontSize: 12, color: colors.textLight, marginTop: 4 }}>
            {field.help_text}
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: colors.bgCard,
          borderRadius: 12,
          width: '90%',
          maxWidth: previewSrc ? 1400 : 700,
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: shadows.elevated,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: 20, borderBottom: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: colors.navy }}>
            {showingTemplateSelector ? 'Dokument erstellen' : (selectedTemplateName || 'Dokument erstellen')}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 8,
              color: colors.textMuted,
            }}
          >
            <FaTimes size={18} />
          </button>
        </div>

        {loadingTemplate ? (
          <div style={{ padding: 60, textAlign: 'center', color: colors.textLight }}>
            Loading template...
          </div>
        ) : showingTemplateSelector ? (
          <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
            <h3 style={{ fontSize: 15, fontWeight: 600, color: colors.navy, marginBottom: 16 }}>
              Wählen Sie ein Template
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {availableTemplates.map(tpl => (
                <div
                  key={tpl.template_key}
                  onClick={() => {
                    setSelectedTemplateKey(tpl.template_key);
                    setSelectedTemplateName(tpl.name);
                  }}
                  style={{
                    padding: 16,
                    border: `1px solid ${colors.border}`,
                    borderRadius: 8,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = colors.bgHover;
                    e.currentTarget.style.borderColor = colors.navy;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = colors.bgCard;
                    e.currentTarget.style.borderColor = colors.border;
                  }}
                >
                  <div style={{ fontWeight: 600, color: colors.navy, marginBottom: 4, fontSize: 14 }}>
                    {tpl.name}
                  </div>
                  {tpl.description && (
                    <div style={{ fontSize: 13, color: colors.textMuted }}>
                      {tpl.description}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : previewSrc ? (
          <div style={{ flex: 1, overflow: 'auto' }}>
            <PreviewPane
              src={previewSrc}
              onRefresh={handlePreview}
              loading={loadingPreview}
            />
          </div>
        ) : (
          <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
            {template && (
              <>
                {template.schema.required_fields?.length > 0 && (
                  <div style={{ marginBottom: 24 }}>
                    <h3 style={{ fontSize: 15, fontWeight: 600, color: colors.navy, marginBottom: 16 }}>
                      Pflichtfelder
                    </h3>
                    {template.schema.required_fields.map(renderField)}
                  </div>
                )}

                {template.schema.optional_fields?.length > 0 && (
                  <div>
                    <h3 style={{ fontSize: 15, fontWeight: 600, color: colors.navy, marginBottom: 16 }}>
                      Optionale Felder
                    </h3>
                    {template.schema.optional_fields.map(renderField)}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <div style={{ padding: 20, borderTop: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between' }}>
          <button
            onClick={onClose}
            disabled={creating}
            style={{
              ...ps.buttonSecondary,
              cursor: creating ? 'not-allowed' : 'pointer',
            }}
          >
            Abbrechen
          </button>

          {!showingTemplateSelector && (
            <div style={{ display: 'flex', gap: 12 }}>
              {!previewSrc && (
                <button
                  onClick={handlePreview}
                  disabled={loadingPreview || creating}
                  style={{
                    ...ps.buttonSecondary,
                    cursor: (loadingPreview || creating) ? 'not-allowed' : 'pointer',
                  }}
                >
                  {loadingPreview ? 'Loading...' : 'Preview'}
                </button>
              )}

              {previewSrc && (
                <button
                  onClick={() => setPreviewSrc(undefined)}
                  disabled={creating}
                  style={{
                    ...ps.buttonSecondary,
                    cursor: creating ? 'not-allowed' : 'pointer',
                  }}
                >
                  Edit
                </button>
              )}

              <button
                onClick={handleCreate}
                disabled={creating || loadingPreview}
                style={{
                  ...(creating || loadingPreview ? ps.buttonDisabled : ps.buttonPrimary),
                }}
              >
                {creating ? 'Creating...' : 'Create'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
